import { useEffect, useState, type ReactNode } from 'react';
import { collection, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { db } from '../../../shared/lib/firebase';
import { useAuth } from '../../auth/useAuth';
import { usePermissions } from '../../../shared/hooks/usePermissions';
import type { WhatsappConversationDoc } from '../types';
import { InboxBadgesContext, type InboxBadges } from './useInboxBadges';

const PAGE_LIMIT = 100;

export function InboxBadgesProvider({ children }: { children: ReactNode }) {
  const { currentUser } = useAuth();
  const { hasPermission, loaded } = usePermissions();
  const uid = currentUser?.uid;
  const canView = loaded && hasPermission('VIEW_WHATSAPP');

  const [triagem, setTriagem] = useState(0);
  const [minhas, setMinhas] = useState(0);

  useEffect(() => {
    if (!uid || !canView) return;
    const q = query(
      collection(db, 'whatsapp_conversations'),
      where('isFinalized', '==', false),
      where('assignedTo', '==', null),
      orderBy('lastActivityAt', 'desc'),
      limit(PAGE_LIMIT)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        let count = 0;
        snap.docs.forEach((d) => {
          const data = d.data() as WhatsappConversationDoc;
          if ((data.unreadCount ?? 0) > 0) count += 1;
        });
        setTriagem(count);
      },
      (err) => {
        console.error('[InboxBadges] triagem snapshot error', err);
        setTriagem(0);
      }
    );
    return unsub;
  }, [uid, canView]);

  useEffect(() => {
    if (!uid || !canView) return;
    const q = query(
      collection(db, 'whatsapp_conversations'),
      where('isFinalized', '==', false),
      where('assignedTo', '==', uid),
      orderBy('lastActivityAt', 'desc'),
      limit(PAGE_LIMIT)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        let count = 0;
        snap.docs.forEach((d) => {
          const data = d.data() as WhatsappConversationDoc;
          if ((data.unreadCount ?? 0) > 0) count += 1;
        });
        setMinhas(count);
      },
      (err) => {
        console.error('[InboxBadges] minhas snapshot error', err);
        setMinhas(0);
      }
    );
    return unsub;
  }, [uid, canView]);

  // Sem permissão/sessão, expõe zero sem precisar resetar estado dentro do effect
  // (a assinatura nem chega a montar). Quando volta a permissão, o snapshot
  // atualiza os contadores.
  const value: InboxBadges = canView
    ? { triagem, minhas, total: triagem + minhas }
    : { triagem: 0, minhas: 0, total: 0 };

  return <InboxBadgesContext.Provider value={value}>{children}</InboxBadgesContext.Provider>;
}
